import { useState, useRef } from 'react';
import api from '../api/client';

export const useYoyAwdUpload = (loadProducts) => {
  const [showAwdUploadPopup, setShowAwdUploadPopup] = useState(false);
  const [selectedAwdFile, setSelectedAwdFile] = useState(null);
  const [parsedAwdData, setParsedAwdData] = useState([]);
  const [uploadAwdStatus, setUploadAwdStatus] = useState(null);
  const awdFileInputRef = useRef(null);

  // Обработка выбора AWD файла
  const handleAwdFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedAwdFile(file);
    setParsedAwdData([]);
    setUploadAwdStatus(null);

    // Проверка типа файла
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExt = file.name.toLowerCase().match(/\.[0-9a-z]+$/)?.[0];
    
    if (!validTypes.includes(fileExt)) {
      setUploadAwdStatus({
        type: 'error',
        message: 'Invalid file type. Please upload CSV or Excel file.'
      });
      return;
    }

    // Проверка размера файла (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setUploadAwdStatus({
        type: 'error',
        message: 'File is too large. Maximum size is 10MB.'
      });
      return;
    }

    setUploadAwdStatus({
      type: 'loading',
      message: 'Parsing file and detecting headers...'
    });

    try {
      // Создаем FormData для отправки
      const formData = new FormData();
      formData.append('file', file);

      // Используем правильный путь к API
      const response = await api.post('/api/upload-awd-data-test', formData);

      // Проверяем, что ответ JSON
      const contentType = response.headers['content-type'];
      if (!contentType || !contentType.includes('application/json')) {
        console.error('Server returned non-JSON response:', String(response.data).substring(0, 200));
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'File parsing failed');
      }

      setParsedAwdData(data.preview || []);
      setUploadAwdStatus({
        type: 'success',
        message: `File parsed successfully: ${data.totalRows || 0} rows found`,
        details: data.preview ? `Preview showing ${Math.min(data.preview.length, 15)} of ${data.totalRows} rows` : ''
      });

    } catch (error) {
      console.error('Error parsing AWD file:', error);
      let message = error.message;
      if (error.response) {
        const contentType = error.response.headers['content-type'];
        if (!contentType || !contentType.includes('application/json')) {
          console.error('Server returned non-JSON response:', String(error.response.data).substring(0, 200));
          message = `Server error: ${error.response.status} ${error.response.statusText}`;
        } else {
          message = error.response.data?.error || error.response.data?.message || 'Failed to parse file';
        }
      }
      setUploadAwdStatus({
        type: 'error',
        message: `Error parsing file: ${message}`,
        details: error.details || 'Please check the file format and try again.'
      });
    }
  };

  // Отправка AWD данных на сервер
  const handleAwdUploadSubmit = async () => {
    if (!selectedAwdFile || parsedAwdData.length === 0) {
      setUploadAwdStatus({
        type: 'error',
        message: ' Please select a valid file first'
      });
      return;
    }

    setUploadAwdStatus({
      type: 'loading',
      message: ' Uploading AWD data to database...'
    });

    try {
      const formData = new FormData();
      formData.append('file', selectedAwdFile);

      const response = await api.post('/api/upload-awd-data', formData);

      // Проверяем, что ответ JSON
      const contentType = response.headers['content-type'];
      if (!contentType || !contentType.includes('application/json')) {
        console.error('Server returned non-JSON response:', String(response.data).substring(0, 200));
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'Upload failed');
      }

      setUploadAwdStatus({
        type: 'success',
        message: ` AWD data uploaded successfully!`,
        details: `Processed: ${data.totalRows || 0} rows | Unique ASINs: ${data.uniqueAsins || 0} | Total Inbound: ${(data.stats?.totalInbound || 0).toLocaleString()} | Total Available: ${(data.stats?.totalAvailable || 0).toLocaleString()}`
      });

      // Очищаем данные через 3 секунды
      setTimeout(() => {
        closeAwdUploadPopup();
        // Обновляем данные если нужно
        if (loadProducts) loadProducts();
      }, 3000);

    } catch (error) {
      console.error('Error uploading AWD data:', error);
      let message = error.message;
      if (error.response) {
        const contentType = error.response.headers['content-type'];
        if (!contentType || !contentType.includes('application/json')) {
          console.error('Server returned non-JSON response:', String(error.response.data).substring(0, 200));
          message = `Server error: ${error.response.status} ${error.response.statusText}`;
        } else {
          message = error.response.data?.error || error.response.data?.message || 'Upload failed';
        }
      }
      setUploadAwdStatus({
        type: 'error',
        message: ` Upload failed: ${message}`,
        details: 'Please check the file format and try again.'
      });
    }
  };

  // Функция для закрытия AWD попапа
  const closeAwdUploadPopup = () => {
    setShowAwdUploadPopup(false);
    setSelectedAwdFile(null);
    setParsedAwdData([]);
    setUploadAwdStatus(null);
    if (awdFileInputRef.current) {
      awdFileInputRef.current.value = '';
    }
  };

  return {
    showAwdUploadPopup,
    setShowAwdUploadPopup,
    selectedAwdFile,
    setSelectedAwdFile,
    parsedAwdData,
    setParsedAwdData,
    uploadAwdStatus,
    setUploadAwdStatus,
    awdFileInputRef,
    handleAwdFileUpload,
    handleAwdUploadSubmit,
    closeAwdUploadPopup
  };
};